import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Check, Close } from '@ankr.com/ui';
import {
  CircularProgress,
  inputBaseClasses,
  TextField,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { makeStyles } from 'tss-react/mui';
import { AbiItem, isAddress } from 'web3-utils';

import { useChainData, useDebounceValue } from 'modules/common';
import { useTranslation } from 'modules/i18n';

import { useLazyGetAbiQuery } from '../../actions/getAbi';
import { useLazyIsCompatibleContractQuery } from '../../actions/isCompatibleContract';
import { convertToAbi } from '../../utils/convertToAbi';
import { isCompatibleAbi } from '../../utils/isCompatibleAbi';
import translation from './abiPanel.translation';

enum Status {
  Loading = 'LOADING',
  Error = 'ERROR',
  Loaded = 'LOADED',
  Compatible = 'COMPATIBLE',
}

export interface OnLoadParams {
  abi: AbiItem[] | null;
  isCompatible: boolean;
  isCustomAbi: boolean;
}

interface Props {
  onLoad: (params: OnLoadParams) => void;
}

interface StatusData {
  color: 'primary' | 'error' | 'success';
  message: string;
  icon?: ReactNode;
}

export function AbiPanel({ onLoad }: Props): JSX.Element {
  const { classes } = useStyles();
  const mountedRef = useRef(false);
  const [status, setStatus] = useState<Status | null>(null);
  const { t, keys } = useTranslation(translation);
  const [getAbi, { isLoading: isAbiLoading }] = useLazyGetAbiQuery();
  const chainData = useChainData();
  const [isCompatible, { isLoading: isCompatibleLoading }] =
    useLazyIsCompatibleContractQuery();
  const isInitializedPanelRef = useRef<boolean>();

  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    resetField,
    setValue,
    watch,
  } = useFormContext();

  const showCustomABI = watch('showCustomAbi');
  const taskContractAddress = watch('taskContractAddress');
  const address = useDebounceValue(taskContractAddress, 500);

  useEffect(() => {
    if (isAbiLoading ?? isCompatibleLoading) {
      setStatus(Status.Loading);
    }
  }, [isAbiLoading, isCompatibleLoading]);

  useEffect(() => {
    if (showCustomABI) {
      return;
    }

    if (!isInitializedPanelRef.current) {
      isInitializedPanelRef.current = true;

      return;
    }

    if (!mountedRef.current ?? !isAddress(address)) {
      onLoad({
        abi: null,
        isCompatible: false,
        isCustomAbi: false,
      });
      return;
    }

    setStatus(Status.Loading);
    void Promise.all([
      getAbi({ address, chainId: chainData?.chainId || null }),
      isCompatible({ contractAddress: address }),
    ]).then(results => {
      if (!mountedRef.current) {
        return;
      }

      const [{ data: abiData, error: abiError }, { data: isCompatibleData }] =
        results;

      const newStatus: Status = (() => {
        if (isCompatibleData) {
          return Status.Compatible;
        }

        if (abiError ?? !abiData) {
          return Status.Error;
        }

        return Status.Loaded;
      })();

      setStatus(newStatus);
      onLoad({
        abi: abiData ?? null,
        isCompatible: !!isCompatibleData,
        isCustomAbi: false,
      });
    });
  }, [
    address,
    chainData?.chainId,
    getAbi,
    isCompatible,
    onLoad,
    showCustomABI,
  ]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const statusData: StatusData | null = useMemo(() => {
    switch (status) {
      case Status.Loading:
        return {
          color: 'primary',
          message: t(keys.loading),
          icon: <CircularProgress size={16} color="primary" />,
        };
      case Status.Error:
        return {
          color: 'error',
          message: t(keys.error),
          icon: <Close color="error" />,
        };
      case Status.Loaded:
        return {
          color: 'success',
          message: t(keys.loaded),
          icon: <Check color="success" />,
        };
      case Status.Compatible:
        return {
          color: 'success',
          message: t(keys.custom),
          icon: <Check color="success" />,
        };
      default:
        return null;
    }
  }, [keys.custom, keys.error, keys.loaded, keys.loading, status, t]);

  return (
    <Box display="flex" flexDirection="column">
      <div className={classes.container}>
        <Box
          display="flex"
          alignItems="center"
          data-tid="create-task-execute-status"
        >
          {statusData && (
            <>
              {statusData.icon}

              <Typography
                variant="body4"
                sx={theme => ({
                  color: theme.palette[statusData.color].main,
                  ml: 1,
                })}
              >
                {statusData.message}
              </Typography>
            </>
          )}
        </Box>

        {status !== Status.Compatible &&
          (!showCustomABI ? (
            <Typography
              variant="button3"
              className={classes.switchButton}
              data-tid="create-task-execute-switch-to-custom-abi"
              onClick={() => {
                setValue('showCustomAbi', true);
                resetField('customAbi');
                onLoad({
                  abi: null,
                  isCompatible: false,
                  isCustomAbi: false,
                });
              }}
            >
              {t(keys.switchToCustom)}
            </Typography>
          ) : (
            <Typography
              variant="button3"
              className={classes.switchButton}
              data-tid="create-task-execute-switch-to-refetch-abi"
              onClick={() => setValue('showCustomAbi', false)}
            >
              {t(keys.refetch)}
            </Typography>
          ))}
      </div>

      {showCustomABI && (
        <Controller
          name="customAbi"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <TextField
              data-tid="create-task-execute-custom-abi-input"
              label={t(keys.customLabel)}
              value={value}
              sx={{ mt: 8 }}
              onChange={event => {
                const customAbiValue = event.target.value;
                try {
                  const abi = convertToAbi(customAbiValue);
                  const isCompatible = isCompatibleAbi(abi);
                  if (isCompatible) {
                    setStatus(Status.Compatible);
                  }
                  onLoad({
                    abi,
                    isCompatible,
                    isCustomAbi: true,
                  });
                  clearErrors('customAbi');
                } catch {
                  setError('customAbi', { message: t(keys.invalidABI) });
                }
                onChange(event);
              }}
              error={!!errors.customAbi}
              helperText={errors.customAbi?.message}
              className={classes.textarea}
              multiline
              rows={6}
            />
          )}
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 24,
    marginTop: theme.spacing(2),
  },
  switchCustomButton: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    marginLeft: theme.spacing(2),
  },
  abiForm: {
    display: 'flex',
  },
  switchButton: {
    '&&': {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      ml: 1,
    },
  },
  textarea: {
    '&&': {
      [`& .${inputBaseClasses.multiline}`]: {
        height: 'auto',
        padding: theme.spacing(3),
      },
    },
  },
}));
